import { useCallback, useEffect, useState } from 'react';
import { useSearchContext } from '@/contexts/SearchContext';
import { API_URLS } from '@/constants';
import { authenticatedFetch } from '../utils/api';

export const useSearchBar = () => {
  const { searchResults, setSearchResults } = useSearchContext();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  const fetchSearchResults = async (searchTerm: string) => {
    try {
      const response = await authenticatedFetch(`${API_URLS.PROMPTS.SEARCH}?q=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      return [];
    }
  };

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleSearchSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Only search if query has at least 2 characters
    if (searchQuery.trim().length >= 2) {
      fetchSearchResults(searchQuery).then((results) => {
        if (results) setSearchResults(results);
      });
    }
  }, [searchQuery, setSearchResults]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400); // 400ms is optimal for search UX

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    // Only search if query has at least 2 characters
    if (debouncedQuery.trim().length >= 2) {
      fetchSearchResults(debouncedQuery).then((results) => {
        if (results) setSearchResults(results);
      });
    } else if (debouncedQuery.trim().length === 0) {
      // Clear search results when query is empty
      setSearchResults([]);
    }
    // For 1 character, do nothing (don't search, don't clear)
  }, [debouncedQuery, setSearchResults]);

  return {
    handleSearchInputChange,
    handleSearchSubmit,
    searchQuery,
    searchResults,
  };
};
